import { useEffect, useState } from "react";
import type { LabResult } from "@ferrivia/api-contract";
import { useApi } from "@ferrivia/auth-web";

export type CustomerLabsState =
  | { status: "loading" }
  | { status: "error"; error: string }
  | { status: "success"; results: LabResult[] };

/* No cache and no polling, unlike usePatient/useChatSummaries — and here
   that is a compliance property, not just an economy. Every call to this
   endpoint writes an asiakastietolaki 10 § käyttöloki row naming the staff
   member who read this customer's results (see
   apps/api/src/modules/labs/store.ts's patientResultDetail). A background
   refresh would put entries in that register that no clinician actually
   performed, which makes the register less truthful, not more complete. One
   fetch per time the pane is opened, which is exactly the disclosure that
   happened.

   Results come back newest draw first, the order the API returns them in. */
export function useCustomerLabs(id: string | undefined): CustomerLabsState {
  const api = useApi();
  const [state, setState] = useState<CustomerLabsState>({ status: "loading" });

  useEffect(() => {
    if (!id) {
      return;
    }
    let cancelled = false;
    setState({ status: "loading" });
    api.patients
      .labs(id)
      .then((results) => {
        if (!cancelled) {
          setState({ status: "success", results });
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setState({ status: "error", error: err instanceof Error ? err.message : "Failed to load lab results" });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [api, id]);

  return state;
}
